/**
 * Appearance control for CloudLens — the same four themes and the same
 * presentation mode the main CloudForge app offers, so the choice a person
 * makes in one product is the choice they get in the other.
 *
 * Phosphor icons are imported straight from dist/csr here rather than through
 * theme/icons.tsx: that module exists to alias Phosphor to the Material names
 * 40 existing call sites already use, and these five have no Material name to
 * alias. dist/csr and not dist/ssr because CloudLens is a client-rendered CRA
 * app, and only './dist/csr/*' is wildcarded in the package's exports map.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, ButtonBase, Divider } from '@mui/material';
import { Palette } from '@phosphor-icons/react/dist/csr/Palette';
import { FileText } from '@phosphor-icons/react/dist/csr/FileText';
import { StackSimple } from '@phosphor-icons/react/dist/csr/StackSimple';
import { Ruler } from '@phosphor-icons/react/dist/csr/Ruler';
import { Eye } from '@phosphor-icons/react/dist/csr/Eye';
import { Presentation } from '@phosphor-icons/react/dist/csr/Presentation';
import { Check } from '@phosphor-icons/react/dist/csr/Check';
import {
  getPresentationMode,
  savePresentationMode,
  saveVisualTheme,
  type VisualTheme,
} from '../theme/visualTheme';

type IconType = typeof FileText;

const OPTIONS: Array<{ id: VisualTheme; label: string; description: string; icon: IconType }> = [
  { id: 'paper', label: 'Paper', description: 'Warm, focused e-ink styling', icon: FileText },
  { id: 'classic', label: 'Classic', description: 'Original CloudForge styling', icon: StackSimple },
  { id: 'blueprint', label: 'Blueprint', description: 'Drafting paper — cyan ink, hairline grid', icon: Ruler },
  { id: 'contrast', label: 'High Contrast', description: 'WCAG AAA — maximum legibility', icon: Eye },
];

interface Props {
  value: VisualTheme;
  onChange: (theme: VisualTheme) => void;
}

const VisualThemeSwitcher: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPresenting(getPresentationMode());
  }, []);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };

    // Capture phase: charts and tables in the dashboard stop propagation of
    // their own pointer events, and a bubble-phase listener would never hear a
    // click that lands on one of them.
    document.addEventListener('pointerdown', closeOnOutsideClick, true);
    return () => document.removeEventListener('pointerdown', closeOnOutsideClick, true);
  }, [open]);

  // One handler for both layers, so their order is decided here rather than by
  // registration order: Escape closes the menu if the menu is open, and
  // otherwise leaves presentation mode. Presentation mode changes how the whole
  // app looks, so it needs the way out that every full-screen-ish mode has.
  useEffect(() => {
    if (!open && !presenting) return;

    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // Don't take Escape from a dialog that is using it to close itself.
      if (document.querySelector('.MuiDialog-root, .MuiModal-root')) return;

      if (open) {
        setOpen(false);
        return;
      }
      setPresenting(false);
      savePresentationMode(false);
    };

    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [open, presenting]);

  const choose = (next: VisualTheme) => {
    onChange(next);
    saveVisualTheme(next);
    setOpen(false);
  };

  const togglePresenting = () => {
    const next = !presenting;
    setPresenting(next);
    savePresentationMode(next);
  };

  const activeLabel = OPTIONS.find((o) => o.id === value)?.label ?? 'Paper';

  const optionSx = (active: boolean) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.25,
    px: 1.25,
    py: 1,
    borderRadius: 1,
    textAlign: 'left' as const,
    border: '1px solid',
    borderColor: active ? 'primary.main' : 'transparent',
    bgcolor: active ? 'action.selected' : 'transparent',
    '&:hover': { bgcolor: 'action.hover' },
  });

  return (
    <Box
      ref={rootRef}
      className="visual-theme-switcher"
      sx={{ position: 'fixed', left: 18, bottom: 18, zIndex: 1250 }}
    >
      {open && (
        <Paper
          role="dialog"
          aria-label="Choose visual style"
          elevation={8}
          sx={{ position: 'absolute', bottom: 'calc(100% + 10px)', left: 0, width: 296, p: 1.25, border: 1, borderColor: 'divider' }}
        >
          <Box sx={{ px: 1.25, pb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Appearance</Typography>
            <Typography variant="caption" color="text.secondary">Choose your workspace style</Typography>
          </Box>

          <Box role="radiogroup" aria-label="Visual style" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {OPTIONS.map((option) => {
              const Icon = option.icon;
              const active = value === option.id;

              return (
                <ButtonBase
                  key={option.id}
                  role="radio"
                  aria-checked={active}
                  onClick={() => choose(option.id)}
                  sx={optionSx(active)}
                >
                  <Box sx={{ display: 'flex', pt: '2px', color: active ? 'primary.main' : 'text.secondary' }}>
                    <Icon aria-hidden="true" size={18} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                      {option.description}
                    </Typography>
                  </Box>
                  {active && (
                    <Box sx={{ display: 'flex', pt: '2px', color: 'primary.main' }}>
                      <Check aria-hidden="true" size={16} weight="bold" />
                    </Box>
                  )}
                </ButtonBase>
              );
            })}
          </Box>

          <Divider sx={{ my: 1 }} />

          <ButtonBase
            role="switch"
            aria-checked={presenting}
            onClick={togglePresenting}
            sx={optionSx(presenting)}
          >
            <Box sx={{ display: 'flex', pt: '2px', color: presenting ? 'primary.main' : 'text.secondary' }}>
              <Presentation aria-hidden="true" size={18} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>Presentation mode</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                Bigger type, dense chrome hidden
              </Typography>
            </Box>
            {presenting && (
              <Box sx={{ display: 'flex', pt: '2px', color: 'primary.main' }}>
                <Check aria-hidden="true" size={16} weight="bold" />
              </Box>
            )}
          </ButtonBase>
        </Paper>
      )}

      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={
          presenting
            ? `Appearance: ${activeLabel}, presenting. Press Escape to exit presentation mode`
            : `Appearance: ${activeLabel}`
        }
        title={presenting ? 'Presenting — Escape to exit' : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          borderRadius: 999,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.primary',
          fontSize: '0.78rem',
          fontWeight: 700,
          boxShadow: 3,
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        {presenting ? <Presentation aria-hidden="true" size={16} /> : <Palette aria-hidden="true" size={16} />}
        <Box component="span">{presenting ? 'Presenting' : activeLabel}</Box>
      </ButtonBase>
    </Box>
  );
};

export default VisualThemeSwitcher;
